import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import {
    CreditCard, Building2, DollarSign, Banknote, CreditCard as CreditCardIcon,
    Loader2, AlertTriangle, ArrowRightLeft,
} from 'lucide-react';
import api from '@/lib/api';
import { formatDate } from '@/lib/datetime';

export default function CustomerCard() {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const status = searchParams.get('status');
    const ref = searchParams.get('ref');

    const [card, setCard] = useState(null);
    const [gym, setGym] = useState(null);
    const [lastCard, setLastCard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Payment state
    const [gymPlans, setGymPlans] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [plansLoading, setPlansLoading] = useState(false);
    const [creatingPayment, setCreatingPayment] = useState(false);
    const [paymentMsg, setPaymentMsg] = useState('');
    const [creatingCashPromise, setCreatingCashPromise] = useState(false);
    const [cashMsg, setCashMsg] = useState('');
    const [creatingTransferPromise, setCreatingTransferPromise] = useState(false);
    const [transferMsg, setTransferMsg] = useState('');

    const dni = user?.dni || '';

    useEffect(() => {
        if (status && ref) {
            const msg = status === 'success'
                ? 'Pago aprobado. Tu tarjeta será activada en breve.'
                : status === 'pending'
                ? 'Pago pendiente de confirmación.'
                : 'El pago no pudo completarse. Intentá de nuevo.';
            setPaymentMsg(msg);
        }
    }, [status, ref]);

    const fetchCard = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await api.get('/customer/card');
            const data = res.data.data;
            // API returns wrapped object { card, gym, last_card } when no active card,
            // or the card object directly when active.
            if (data && 'card' in data) {
                setCard(data.card);
                setGym(data.gym || null);
                setLastCard(data.last_card || null);
            } else {
                setCard(data);
                setGym(data?.gym || null);
                setLastCard(null);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error al cargar la tarjeta.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCard();
    }, []);

    const fetchPaymentData = async (gymId) => {
        setPlansLoading(true);
        try {
            const [plansRes, methodsRes] = await Promise.all([
                api.get(`/public/gym/${gymId}/plans`),
                api.get(`/public/gym/${gymId}/payment-methods`),
            ]);
            setGymPlans(plansRes.data.data || []);
            setPaymentMethods(methodsRes.data.data || []);
        } catch {
            setGymPlans([]);
            setPaymentMethods([]);
        } finally {
            setPlansLoading(false);
        }
    };

    useEffect(() => {
        const isExpired = !card || (card.end_date && new Date(card.end_date) < new Date()) || (card && card.total_classes > 0 && card.used_classes >= card.total_classes);
        if (isExpired && gym?.id) {
            fetchPaymentData(gym.id);
        }
    }, [card, gym?.id]);

    const handlePayWithMp = async (planId) => {
        if (!dni) return;
        setCreatingPayment(true);
        setCashMsg('');
        try {
            const res = await api.post('/public/create-payment-preference', {
                dni,
                gym_plan_id: planId,
                return_path: '/mi-cuenta/tarjeta',
            });
            const initPoint = res.data.data?.init_point;
            if (initPoint) {
                window.location.href = initPoint;
            }
        } catch (err) {
            setPaymentMsg(err.response?.data?.message || 'Error al crear el pago.');
        } finally {
            setCreatingPayment(false);
        }
    };

    const handleCashPayment = async (planId) => {
        if (!dni) return;
        setCreatingCashPromise(true);
        setCashMsg('');
        try {
            const res = await api.post('/public/create-cash-promise', {
                dni,
                gym_plan_id: planId,
            });
            setCashMsg(res.data.data?.message || 'Tu tarjeta fue renovada. Acercate al gimnasio para abonar en efectivo.');
            fetchCard();
        } catch (err) {
            setPaymentMsg(err.response?.data?.message || 'Error al crear la promesa de pago.');
        } finally {
            setCreatingCashPromise(false);
        }
    };

    const handleTransferPayment = async (planId) => {
        if (!dni) return;
        setCreatingTransferPromise(true);
        setTransferMsg('');
        try {
            const res = await api.post('/public/create-transfer-promise', {
                dni,
                gym_plan_id: planId,
            });
            setTransferMsg(res.data.data?.message || 'Tu tarjeta fue renovada. Realizá la transferencia y el gimnasio la confirmará.');
            fetchCard();
        } catch (err) {
            setPaymentMsg(err.response?.data?.message || 'Error al crear la promesa de transferencia.');
        } finally {
            setCreatingTransferPromise(false);
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(price);
    };

    if (loading) return <p className="text-white/60 text-center py-8">Cargando...</p>;

    if (error) {
        return (
            <div>
                <h1 className="text-2xl font-bold uppercase tracking-wide mb-6">Mi Tarjeta</h1>
                <Card className="bg-[#0a0a0a] border-hairline-dark">
                    <CardContent className="py-12 text-center">
                        <CreditCard className="h-12 w-12 text-white/60 mx-auto mb-4" />
                        <p className="text-white/60">{error}</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const progress = card && card.total_classes > 0 ? (card.used_classes / card.total_classes) * 100 : 0;
    const remaining = card ? Math.max(0, card.total_classes - card.used_classes) : 0;

    const isCardExpired = !card || (card.end_date && new Date(card.end_date) < new Date()) || remaining <= 0;

    return (
        <div>
            <h1 className="text-2xl font-bold uppercase tracking-wide mb-6">Mi Tarjeta</h1>

            {paymentMsg && (
                <div className={`text-sm p-4 rounded-md mb-6 border ${status === 'success' ? 'border-green-800 bg-green-950/30 text-green-300' : status === 'pending' ? 'border-yellow-800 bg-yellow-950/30 text-yellow-300' : 'border-red-800 bg-red-950/30 text-red-300'}`}>
                    {paymentMsg}
                </div>
            )}

            {card ? (
                <Card className="max-w-lg mx-auto overflow-hidden bg-[#0a0a0a] border-hairline-dark">
                    <div
                        className="p-6 relative overflow-hidden"
                        style={{
                            backgroundColor: gym?.card_bg_color || undefined,
                            color: gym?.card_text_color || undefined,
                            backgroundImage: gym?.card_banner ? `url(/storage/${gym.card_banner})` : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            ...(!gym?.card_bg_color && !gym?.card_banner ? { backgroundColor: '#0a0a0a', color: '#ffffff' } : {}),
                        }}
                    >
                        {gym?.card_banner && <div className="absolute inset-0 bg-black/30" />}
                        <div className="relative z-10">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    {gym?.logo ? (
                                        <img src={`/storage/${gym.logo}`} alt="Logo" className="h-10 w-10 object-contain rounded bg-white/10 p-1" />
                                    ) : (
                                        <div className="h-10 w-10 bg-white/10 rounded flex items-center justify-center">
                                            <Building2 className="h-5 w-5" />
                                        </div>
                                    )}
                                    <span className="font-semibold text-lg">{gym?.name || 'Gimnasio'}</span>
                                </div>
                                <Badge variant={card.status === 'active' ? 'success' : 'secondary'} className="text-xs">
                                    {card.status === 'active' ? 'ACTIVA' : 'VENCIDA'}
                                </Badge>
                            </div>
                            <div className="text-3xl font-bold tracking-wider mb-1">
                                GYMS.ar
                            </div>
                            <p className="text-sm opacity-80">Tarjeta de membresía</p>
                        </div>
                    </div>

                    <CardContent className="p-6 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-xs text-white/60 uppercase">Inicio</p>
                                <p className="font-semibold">{formatDate(card.start_date)}</p>
                            </div>
                            <div>
                                <p className="text-xs text-white/60 uppercase">Vencimiento</p>
                                <p className="font-semibold">{formatDate(card.end_date)}</p>
                            </div>
                        </div>

                        <div>
                            <div className="flex justify-between text-sm mb-2">
                                <span className="text-white/60">Clases utilizadas</span>
                                <span className="font-semibold">{card.used_classes} / {card.total_classes}</span>
                            </div>
                            <div className="w-full bg-white/10 rounded-full h-3">
                                <div
                                    className="bg-white rounded-full h-3 transition-all"
                                    style={{ width: `${Math.min(progress, 100)}%` }}
                                />
                            </div>
                            <p className="text-sm text-white/60 mt-1">
                                {remaining} {remaining === 1 ? 'clase restante' : 'clases restantes'}
                            </p>
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <Card className="overflow-hidden bg-[#0a0a0a] border-hairline-dark">
                    <div
                        className="p-6 relative overflow-hidden"
                        style={{
                            backgroundColor: gym?.card_bg_color || undefined,
                            color: gym?.card_text_color || undefined,
                            backgroundImage: gym?.card_banner ? `url(/storage/${gym.card_banner})` : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            ...(!gym?.card_bg_color && !gym?.card_banner ? { backgroundColor: '#0a0a0a', color: '#ffffff' } : {}),
                        }}
                    >
                        {gym?.card_banner && <div className="absolute inset-0 bg-black/50" />}
                        <div className="relative z-10">
                            <div className="flex items-center gap-3 mb-3">
                                {gym?.logo ? (
                                    <img src={`/storage/${gym.logo}`} alt="Logo" className="h-10 w-10 object-contain rounded bg-white/10 p-1" />
                                ) : (
                                    <div className="h-10 w-10 bg-white/10 rounded flex items-center justify-center">
                                        <Building2 className="h-5 w-5 text-white" />
                                    </div>
                                )}
                                <span className="font-semibold text-lg text-white">{gym?.name || 'Gimnasio'}</span>
                            </div>
                            <p className="text-2xl font-bold text-white">{user?.name || 'Alumno'}</p>
                        </div>
                    </div>

                    <CardContent className="p-6">
                        <div className="text-center py-4">
                            <CreditCard className="h-8 w-8 text-white/60 mx-auto mb-2" />
                            <p className="text-white/60">No tenés una tarjeta activa en este momento.</p>
                            {lastCard && (
                                <p className="text-sm text-white/60 mt-2">
                                    Última tarjeta vencida: <span className="font-medium text-white">{formatDate(lastCard.end_date)}</span>
                                </p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Payment section — only when card is expired or absent */}
            {isCardExpired && (
                <Card className="mt-6 bg-[#0a0a0a] border-hairline-dark">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg text-white uppercase tracking-wide">
                            <DollarSign className="h-5 w-5 text-white" />
                            Renová tu Membresía
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {plansLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin text-white" />
                            </div>
                        ) : gymPlans.length === 0 ? (
                            <div className="text-center py-6 text-white/40">
                                <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
                                <p className="text-sm">Tu gimnasio aún no tiene planes de suscripción disponibles.</p>
                                <p className="text-xs mt-1">Contactá al gimnasio para más información.</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {cashMsg && (
                                    <div className="text-sm p-4 rounded-md border border-green-800 bg-green-950/30 text-green-300 mb-4">
                                        {cashMsg}
                                    </div>
                                )}
                                {transferMsg && (
                                    <div className="text-sm p-4 rounded-md border border-blue-800 bg-blue-950/30 text-blue-300 mb-4">
                                        {transferMsg}
                                    </div>
                                )}
                                {gymPlans.map(plan => (
                                    <div key={plan.id} className="p-4 rounded-lg border border-hairline-dark hover:bg-white/5 transition-colors">
                                        <div className="flex items-start justify-between mb-2">
                                            <div>
                                                <h4 className="font-bold text-white">{plan.name}</h4>
                                                <p className="text-xs text-white/60">{plan.description || `${plan.duration_months} mes(es)`}</p>
                                            </div>
                                            <span className="text-lg font-bold text-white">{formatPrice(plan.price)}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-white/50 mb-3">
                                            <span>{plan.duration_months} {plan.duration_months === 1 ? 'mes' : 'meses'}</span>
                                            <span className="text-white/20">|</span>
                                            <span>{plan.total_classes > 0 ? `${plan.total_classes} clases` : 'Clases ilimitadas'}</span>
                                        </div>
                                        <div className="flex gap-2 flex-wrap">
                                            {paymentMethods.find(m => m.id === 'mercadopago') && (
                                                <Button
                                                    size="sm"
                                                    className="flex-1 uppercase tracking-widest text-[10px]"
                                                    disabled={creatingPayment}
                                                    onClick={() => handlePayWithMp(plan.id)}
                                                >
                                                    <CreditCardIcon className="h-3.5 w-3.5 mr-1.5" />
                                                    {creatingPayment ? 'Procesando...' : 'Pagar con MercadoPago'}
                                                </Button>
                                            )}
                                            {paymentMethods.find(m => m.id === 'cash') && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="flex-1 uppercase tracking-widest text-[10px] border-white/30 text-white hover:bg-white/10"
                                                    disabled={creatingCashPromise}
                                                    onClick={() => handleCashPayment(plan.id)}
                                                >
                                                    <Banknote className="h-3.5 w-3.5 mr-1.5" />
                                                    {creatingCashPromise ? 'Procesando...' : 'Pagar en Efectivo'}
                                                </Button>
                                            )}
                                            {paymentMethods.find(m => m.id === 'transfer') && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="flex-1 uppercase tracking-widest text-[10px] border-white/30 text-white hover:bg-white/10"
                                                    disabled={creatingTransferPromise}
                                                    onClick={() => handleTransferPayment(plan.id)}
                                                >
                                                    <ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" />
                                                    {creatingTransferPromise ? 'Procesando...' : 'Transferencia'}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
